import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";

const normalize = (v) =>
  String(v ?? "")
    .trim()
    .replace(/\s+/g, "")         // remove spaces
    .replace(/[^0-9A-Za-z]/g, "")// remove stray characters
    .toUpperCase();              // uniform casing

const ProductAuth = () => {
  const [authCodeSet, setAuthCodeSet] = useState(new Set());
  const [serialSet, setSerialSet] = useState(new Set());
  const [input, setInput] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        // Make sure olampian.xlsx is placed in your project's /public folder
        const res = await fetch("/olampian.xlsx", { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.arrayBuffer();

        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];

        // Keep raw numbers; fill blanks with ""
        const rows = XLSX.utils.sheet_to_json(sheet, { raw: true, defval: "" });

        const aSet = new Set();
        const sSet = new Set();

        for (const row of rows) {
          // Exact headers from your file:
          // "AuthenticCode" (9-digit) and "Seriel Num" (string like ONL00001)
          const rawAuth =
            row["AuthenticCode"] ??
            row["Authenticcode"] ??
            row["AUTHENTICCODE"] ??
            row["Authentic Code"] ??
            "";
          const rawSerial =
            row["Seriel Num"] ??
            row["Serial Num"] ??
            row["Serial"] ??
            row["SerielNum"] ??
            "";

          const auth = normalize(rawAuth);
          const serial = normalize(rawSerial);

          if (auth) aSet.add(auth);
          if (serial) sSet.add(serial);
        }

        setAuthCodeSet(aSet);
        setSerialSet(sSet);
        console.log("✅ Loaded codes:", { authCount: aSet.size, serialCount: sSet.size });
      } catch (err) {
        console.error("❌ Error loading Excel file:", err);
        setResult({
          type: "error",
          msg: "Failed to load product codes. Please try again or check the file path.",
        });
      }
    })();
  }, []);

  const handleVerify = () => {
    const clean = normalize(input);

    if (!clean) {
      setResult({ type: "warning", msg: "⚠️ Please enter a product code" });
      return;
    }

    // Heuristic: if it’s 9 digits, likely an AuthenticCode. If it starts with ONL, likely serial.
    const isNineDigits = /^\d{9}$/.test(clean);
    const isSerialLike = /^ONL/i.test(clean);

    if (authCodeSet.has(clean)) {
      setResult({ type: "success", msg: "✅ Valid Product — Authentication Successful (AuthenticCode)." });
      return;
    }
    if (serialSet.has(clean)) {
      setResult({ type: "success", msg: "✅ Valid Product — Authentication Successful (Serial No.)." });
      return;
    }

    // Extra hint to users who might be typing the other field
    if (isNineDigits && serialSet.size > 0) {
      setResult({
        type: "error",
        msg: "❌ Code not found. Tip: If you entered the 9-digit code, ensure it’s the AuthenticCode (not the Serial No.).",
      });
    } else if (isSerialLike && authCodeSet.size > 0) {
      setResult({
        type: "error",
        msg: "❌ Code not found. Tip: If you entered the Serial No. (e.g., ONL00001), try the 9-digit AuthenticCode instead.",
      });
    } else {
      setResult({ type: "error", msg: "❌ Invalid Product — Code not found!" });
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2>Product Authentication</h2>
        <input
          type="text"
          placeholder="Enter 9-digit AuthenticCo"
          value={input}
          onChange={(e) => setInput(e.target.value)}
        />
        <button onClick={handleVerify}>Verify Product</button>
        {result && <p className={`result ${result.type}`}>{result.msg}</p>}
      </div>
    </div>
  );
};

export default ProductAuth;

